import type { SupersearchItem } from '@/types/supersearch'
import { getProjectRoute } from './routeHelpers'

/**
 * Convert backend API link to frontend route path
 * Maps /api/... -> frontend path
 * @param link - The API link to convert
 * @param item - Optional item data for context-aware routing (e.g., for projects)
 */
export function convertApiLinkToRoute(link: string, item?: SupersearchItem['item']): string {
  // Remove /api prefix if present
  const path = link.replace(/^\/api/, '')
  
  // Handle products (API may use /products/, legacy /pim/products/, or /product-dev/pim/)
  if (
    path.startsWith('/products/') ||
    path.startsWith('/pim/products/') ||
    path.startsWith('/product-dev/pim/')
  ) {
    const uuid = path
      .replace(/^\/product-dev\/pim\//, '')
      .replace(/^\/pim\/products\//, '')
      .replace(/^\/products\//, '')
      .split('/')[0]
      .split('?')[0]
    return `/product-dev/pim/${uuid}`
  }
  
  // Handle accounts (can be /accounts/ or /crm/accounts/)
  if (path.startsWith('/accounts/') || path.startsWith('/crm/accounts/')) {
    const uuid = path.replace(/^\/crm\/accounts\//, '').replace(/^\/accounts\//, '').split('/')[0].split('?')[0]
    return `/crm/accounts/${uuid}`
  }
  
  // Handle sub-accounts (can be /sub-accounts/ or /crm/sub-accounts/)
  if (path.startsWith('/sub-accounts/') || path.startsWith('/crm/sub-accounts/')) {
    const uuid = path.replace(/^\/crm\/sub-accounts\//, '').replace(/^\/sub-accounts\//, '').split('/')[0].split('?')[0]
    return `/crm/sub-accounts/${uuid}`
  }
  
  // Handle tasks (can be /tasks/ or /crm/tasks/)
  if (path.startsWith('/tasks/') || path.startsWith('/crm/tasks/')) {
    const uuid = path.replace(/^\/crm\/tasks\//, '').replace(/^\/tasks\//, '').split('/')[0].split('?')[0]
    // Check if it's a CRM task or personal task
    // For now, default to CRM task - can be enhanced based on item data
    return `/crm/tasks/${uuid}`
  }
  
  // Handle projects (can be /projects/ or /crm/projects/)
  if (path.startsWith('/projects/') || path.startsWith('/crm/projects/')) {
    const uuid = path.replace(/^\/crm\/projects\//, '').replace(/^\/projects\//, '').split('/')[0].split('?')[0]
    // Use getProjectRoute if item data is available for context-aware routing
    if (item && item.uuid) {
      return getProjectRoute({
        uuid: item.uuid,
        context: item.context as string | undefined,
        projectable_type: item.projectable_type as string | undefined
      })
    }
    // Fallback to CRM route if no item data
    return `/crm/projects/${uuid}`
  }
  
  // Handle addresses (can be /addresses/ or /crm/addresses/)
  if (path.startsWith('/addresses/') || path.startsWith('/crm/addresses/')) {
    const id = path.replace(/^\/crm\/addresses\//, '').replace(/^\/addresses\//, '').split('/')[0].split('?')[0]
    // Addresses might not have detail pages, redirect to CRM or account
    return `/crm`
  }
  
  // Handle notes (can be /notes/ or /crm/notes/ or /my/notes/)
  if (path.startsWith('/notes/') || path.startsWith('/crm/notes/') || path.startsWith('/my/notes/')) {
    const id = path.replace(/^\/my\/notes\//, '').replace(/^\/crm\/notes\//, '').replace(/^\/notes\//, '').split('/')[0].split('?')[0]
    // Notes can be personal or CRM - check if it's personal note
    // For now, default to personal note route
    return `/my/notes/${id}`
  }
  
  // Handle users (can be /users/ or /admin/users/)
  if (path.startsWith('/users/') || path.startsWith('/admin/users/')) {
    const uuid = path.replace(/^\/admin\/users\//, '').replace(/^\/users\//, '').split('/')[0].split('?')[0]
    return `/admin/users/${uuid}`
  }
  
  // Handle bug-reports
  if (path.startsWith('/bug-reports/')) {
    const id = path.replace('/bug-reports/', '').split('/')[0].split('?')[0]
    return `/bug-reports/${id}`
  }
  
  // Handle design-jobs
  if (path.startsWith('/design-jobs/')) {
    const uuid = path.replace('/design-jobs/', '').split('/')[0].split('?')[0]
    return `/design-jobs/${uuid}`
  }
  
  // Handle inquiries (Amazon)
  if (path.startsWith('/inquiries/')) {
    const uuid = path.replace('/inquiries/', '').split('/')[0].split('?')[0]
    return `/inquiries/${uuid}`
  }
  
  // Handle local-inquiries
  if (path.startsWith('/local-inquiries/')) {
    const uuid = path.replace('/local-inquiries/', '').split('/')[0].split('?')[0]
    return `/local-inquiries/${uuid}`
  }
  
  // Handle walmart-inquiries
  if (path.startsWith('/walmart-inquiries/')) {
    const uuid = path.replace('/walmart-inquiries/', '').split('/')[0].split('?')[0]
    return `/walmart-inquiries/${uuid}`
  }
  
  // Default fallback
  return '/home'
}

/**
 * Get display text for a supersearch item
 */
export function getItemDisplayText(item: SupersearchItem['item']): string {
  // Check if this is a summary result (for sales-history and operations-jobs)
  if (item.summary && item.display_text) {
    return item.display_text
  }
  
  // Operations jobs: prefer job_no, then title
  if (item.job_no) {
    return item.title ? `${item.job_no} - ${item.title}` : item.job_no
  }
  
  // Sales history: prefer job_no, then job_title
  if (item.job_title) {
    return item.job_no ? `${item.job_no} - ${item.job_title}` : item.job_title
  }
  
  // Inquiries: prefer inquiry_number
  if (item.inquiry_number !== undefined && item.inquiry_number !== null) {
    return `Inquiry #${item.inquiry_number}`
  }
  
  // Default: name, title, label, sku, email
  return item.name || item.title || item.label || item.sku || item.email || 'Untitled'
}

/**
 * Get secondary text for a supersearch item based on domain
 */
export function getItemSecondaryText(item: SupersearchItem['item'], domain: string): string {
  switch (domain) {
    case 'products':
      return item.sku || item.label || ''
    case 'accounts':
    case 'sub-accounts':
      return item.email || item.status || ''
    case 'tasks':
    case 'projects':
      return item.status || ''
    case 'users':
      return item.email || ''
    case 'operations-jobs':
      return item.job_no || item.order_no || item.production_status || ''
    case 'sales-history':
      return item.job_no || item.job_title || item.order_status || item.account_name || ''
    case 'design-jobs':
      return item.status || item.priority || ''
    case 'inquiries': {
      // Amazon inquiries: site_code - point_of_contact
      const parts = []
      if (item.site_code) parts.push(item.site_code)
      if (item.point_of_contact) parts.push(item.point_of_contact)
      return parts.join(' - ') || item.status || ''
    }
    case 'local-inquiries': {
      // Local inquiries: company_name - point_of_contact
      const localParts = []
      if (item.company_name) localParts.push(item.company_name)
      if (item.point_of_contact) localParts.push(item.point_of_contact)
      return localParts.join(' - ') || item.status || ''
    }
    case 'walmart-inquiries': {
      // Walmart inquiries: site_code - primary_point_of_contact
      const walmartParts = []
      if (item.site_code) walmartParts.push(item.site_code)
      if (item.primary_point_of_contact) walmartParts.push(item.primary_point_of_contact)
      return walmartParts.join(' - ') || item.status || ''
    }
    default:
      return item.status || item.content?.substring(0, 50) || ''
  }
}

